import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { exportCombinedPdf } from "../admin/pdfExport";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RETURN_EMAIL = "info@theravensquill.co.za";

const slug = (s) =>
  (s || "").toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 60) || "document";

const GUIDE_BG = `url("data:image/svg+xml,${encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" width="800" height="300"><line x1="40" y1="224" x2="760" y2="224" stroke="#C4BCAE" stroke-width="1.5" stroke-dasharray="6 6"/><text x="22" y="238" font-family="serif" font-size="30" fill="#C4BCAE">\u2715</text></svg>`
)}")`;

const SignaturePad = ({ canvasRef, onChange }) => {
  const drawing = useRef(false);
  const last = useRef({ x: 0, y: 0 });
  const sized = useRef(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || sized.current) return;
    const ratio = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    if (!rect.width || !rect.height) return; // not laid out yet; a later run will size it
    sized.current = true;
    canvas.width = rect.width * ratio;
    canvas.height = rect.height * ratio;
    const ctx = canvas.getContext("2d");
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 3.4;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#1C1A17";
  });

  const pos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const src = e.touches ? e.touches[0] : e;
    return { x: src.clientX - rect.left, y: src.clientY - rect.top };
  };
  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    last.current = pos(e);
  };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const p = pos(e);
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
    onChange();
  };
  const end = () => {
    drawing.current = false;
  };

  return (
    <canvas
      ref={canvasRef}
      data-testid="signature-canvas"
      className="block w-full border-2 border-[#7A2E2E] touch-none cursor-crosshair"
      style={{
        height: "300px",
        touchAction: "none",
        backgroundColor: "#FFFFFF",
        backgroundImage: GUIDE_BG,
        backgroundSize: "100% 100%",
        backgroundRepeat: "no-repeat",
      }}
      onMouseDown={start}
      onMouseMove={move}
      onMouseUp={end}
      onMouseLeave={end}
      onTouchStart={start}
      onTouchMove={move}
      onTouchEnd={end}
    />
  );
};

const Frame = ({ children }) => (
  <div className="min-h-screen bg-[#F4F1EA] text-[#1C1A17] px-5 py-12" data-testid="sign-page">
    <div className="max-w-[760px] mx-auto">{children}</div>
  </div>
);

const Masthead = () => (
  <div className="text-center mb-10">
    <div className="text-[11px] uppercase tracking-[0.32em] text-[#7A2E2E]">The Raven&rsquo;s Quill</div>
    <div className="mx-auto mt-3 h-px w-16 bg-[#7A2E2E]" />
  </div>
);

const SignDocument = () => {
  const { token } = useParams();
  const canvasRef = useRef(null);
  const previewIframeRef = useRef(null);
  const [state, setState] = useState({ loading: true, error: "", doc: null });
  const [name, setName] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [hasInk, setHasInk] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [formError, setFormError] = useState("");
  const [pdfBusy, setPdfBusy] = useState(false);

  const downloadForPrinting = async () => {
    if (!previewIframeRef.current || pdfBusy) return;
    setPdfBusy(true);
    try {
      await exportCombinedPdf({
        iframe: previewIframeRef.current,
        filename: `${slug(state.doc.title)}-to-sign.pdf`,
      });
    } finally {
      setPdfBusy(false);
    }
  };

  const today = new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/sign/${token}`);
        if (!res.ok) {
          const e = await res.json().catch(() => ({}));
          throw new Error(e.detail || "This signing link is invalid or has expired.");
        }
        const doc = await res.json();
        setState({ loading: false, error: "", doc });
        if (doc.client_name) setName(doc.client_name);
        if (doc.status === "signed") setDone(true);
      } catch (e) {
        setState({ loading: false, error: e.message, doc: null });
      }
    })();
  }, [token]);

  const clearSignature = () => {
    const c = canvasRef.current;
    if (!c) return;
    c.getContext("2d").clearRect(0, 0, c.width, c.height);
    setHasInk(false);
  };

  const submit = async () => {
    setFormError("");
    if (!name.trim()) return setFormError("Please type your full name.");
    if (!hasInk) return setFormError("Please draw your signature in the box.");
    if (!agreed) return setFormError("Please confirm that you have read and agree.");
    setSubmitting(true);
    try {
      const signature = canvasRef.current.toDataURL("image/png");
      const res = await fetch(`${API}/sign/${token}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ signer_name: name.trim(), signature_data_url: signature, agreed }),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.detail || "Something went wrong. Please try again.");
      }
      setDone(true);
    } catch (e) {
      setFormError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (state.loading) {
    return (
      <Frame>
        <Masthead />
        <p className="text-center italic text-[#6E6A5E]" data-testid="sign-loading">Preparing your document&hellip;</p>
      </Frame>
    );
  }

  if (state.error) {
    return (
      <Frame>
        <Masthead />
        <p className="text-center text-[#7A2E2E]" data-testid="sign-error">{state.error}</p>
      </Frame>
    );
  }

  if (done) {
    return (
      <Frame>
        <Masthead />
        <div className="text-center" data-testid="sign-complete">
          <h1 className="text-3xl italic mb-4">Thank you.</h1>
          <p className="text-[#6E6A5E] leading-relaxed max-w-[52ch] mx-auto">
            Your {state.doc.title} has been signed and returned to The Raven&rsquo;s Quill. A copy of the
            confirmation has been sent to your email. You may now close this page.
          </p>
        </div>
      </Frame>
    );
  }

  return (
    <Frame>
      <Masthead />
      <div className="text-center mb-8">
        <p className="text-[11px] uppercase tracking-[0.28em] text-[#7A2E2E] mb-2">For your review and signature</p>
        <h1 className="text-3xl sm:text-4xl italic">{state.doc.title}</h1>
      </div>

      <div className="border border-[#A8A296] bg-white mb-10" data-testid="sign-document-preview">
        <iframe
          ref={previewIframeRef}
          title={state.doc.title}
          srcDoc={state.doc.document_html}
          sandbox="allow-same-origin"
          className="w-full block"
          style={{ height: "1200px", border: "0" }}
          onLoad={(e) => {
            try {
              const d = e.target.contentDocument;
              if (d && d.body) e.target.style.height = d.body.scrollHeight + 40 + "px";
            } catch (_) {}
          }}
        />
      </div>

      <div className="border-t-2 border-[#7A2E2E] pt-8">
        <h2 className="text-[15px] uppercase tracking-[0.24em] text-[#7A2E2E] mb-3">Sign here</h2>
        <p className="text-[17px] leading-relaxed text-[#1C1A17] mb-8 max-w-[60ch]">
          There are three simple steps. Please take your time, there is no rush.
        </p>

        {/* Step 1: name */}
        <div className="mb-10">
          <div className="flex items-center gap-3 mb-3">
            <span className="flex items-center justify-center w-8 h-8 rounded-full bg-[#7A2E2E] text-[#F4F1EA] text-[16px]" data-testid="step-1-badge">1</span>
            <label className="text-[18px] text-[#1C1A17]" htmlFor="signer-name">Type your full name</label>
          </div>
          <input
            id="signer-name"
            data-testid="signer-name-input"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="For example, Thandiwe Mokoena"
            className="w-full bg-white border-2 border-[#A8A296] px-4 py-4 text-[20px] focus:outline-none focus:border-[#7A2E2E]"
          />
        </div>

        {/* Step 2: signature */}
        <div className="mb-10">
          <div className="flex items-center gap-3 mb-3">
            <span className="flex items-center justify-center w-8 h-8 rounded-full bg-[#7A2E2E] text-[#F4F1EA] text-[16px]" data-testid="step-2-badge">2</span>
            <span className="text-[18px] text-[#1C1A17]">Draw your signature in the box</span>
          </div>
          <p className="text-[16px] leading-relaxed text-[#6E6A5E] mb-4 max-w-[60ch]">
            Use your finger on a phone or tablet, or hold down the mouse button and move it on a computer.
            Sign above the dotted line, just as you would on paper. If you are not happy with it, press
            <span className="text-[#7A2E2E]"> Clear </span> and try again.
          </p>
          <div className="relative">
            <SignaturePad canvasRef={canvasRef} onChange={() => setHasInk(true)} />
            {!hasInk && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <span className="text-[#C4BCAE] italic" style={{ fontSize: "22px" }}>Draw your signature here</span>
              </div>
            )}
          </div>
          <div className="mt-4">
            <button
              type="button"
              onClick={clearSignature}
              data-testid="clear-signature"
              className="border-2 border-[#7A2E2E] text-[#7A2E2E] px-6 py-3 text-[15px] uppercase tracking-[0.14em] hover:bg-[#7A2E2E] hover:text-[#F4F1EA] transition-colors"
            >
              Clear and start again
            </button>
          </div>
          <p className="text-[16px] text-[#6E6A5E] mt-6">Today&rsquo;s date: <span className="text-[#1C1A17]">{today}</span></p>
        </div>

        {/* Step 3: agree */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-3">
            <span className="flex items-center justify-center w-8 h-8 rounded-full bg-[#7A2E2E] text-[#F4F1EA] text-[16px]" data-testid="step-3-badge">3</span>
            <span className="text-[18px] text-[#1C1A17]">Confirm and submit</span>
          </div>
          <label className="flex items-start gap-4 p-4 border-2 border-[#A8A296] bg-white cursor-pointer select-none" data-testid="agree-label">
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              data-testid="agree-checkbox"
              className="mt-1 h-6 w-6 accent-[#7A2E2E] shrink-0"
            />
            <span className="text-[17px] leading-relaxed">
              I confirm that I have read the {state.doc.title} in full and I agree to be bound by its terms.
            </span>
          </label>
        </div>

        {formError && (
          <p className="text-[17px] text-[#7A2E2E] italic mt-4 mb-2" data-testid="sign-form-error">{formError}</p>
        )}

        <button
          type="button"
          onClick={submit}
          disabled={submitting}
          data-testid="sign-submit"
          className="mt-6 w-full sm:w-auto bg-[#1C1A17] text-[#F4F1EA] px-12 py-5 text-[16px] uppercase tracking-[0.2em] transition-colors hover:bg-[#7A2E2E] disabled:opacity-60"
        >
          {submitting ? "Submitting\u2026" : "Sign & submit"}
        </button>
        <p className="text-[15px] text-[#6E6A5E] mt-4 max-w-[60ch]">
          When you press this button, your signed document is sent straight back to The Raven&rsquo;s Quill.
        </p>
      </div>

      <div className="border-t-2 border-[#7A2E2E] mt-16 pt-8" data-testid="sign-on-paper-section">
        <h2 className="text-[15px] uppercase tracking-[0.24em] text-[#7A2E2E] mb-3">Prefer to sign on paper?</h2>
        <p className="text-[17px] leading-relaxed text-[#1C1A17] mb-7 max-w-[60ch]">
          You are welcome to sign by hand instead. Download the document, print it, sign it, and send it
          back to us. Here is how, step by step:
        </p>

        <ol className="mb-9 max-w-[62ch] space-y-5">
          {[
            "Download the document as a PDF using the button below.",
            "Print it on any printer, at home or at a print shop.",
            "Sign it by hand on the signature line, and fill in the date and place.",
            "Scan the signed pages, or take a clear, well-lit photo of every page.",
            <>
              Email the signed copy to{" "}
              <a href={`mailto:${RETURN_EMAIL}`} className="text-[#7A2E2E] underline">{RETURN_EMAIL}</a>.
            </>,
          ].map((text, i) => (
            <li key={i} className="flex items-start gap-4">
              <span className="flex items-center justify-center w-8 h-8 rounded-full bg-[#7A2E2E] text-[#F4F1EA] text-[16px] shrink-0" data-testid={`paper-step-${i + 1}`}>
                {i + 1}
              </span>
              <span className="text-[17px] leading-relaxed text-[#1C1A17]">{text}</span>
            </li>
          ))}
        </ol>

        <button
          type="button"
          onClick={downloadForPrinting}
          disabled={pdfBusy}
          data-testid="download-document-pdf"
          className="w-full sm:w-auto border-2 border-[#1C1A17] text-[#1C1A17] px-10 py-4 text-[15px] uppercase tracking-[0.18em] transition-colors hover:bg-[#1C1A17] hover:text-[#F4F1EA] disabled:opacity-60"
        >
          {pdfBusy ? "Preparing PDF\u2026" : "Download the document (PDF)"}
        </button>
        <p className="text-[15px] text-[#6E6A5E] mt-5 max-w-[62ch]">
          Please send the signed document to{" "}
          <a href={`mailto:${RETURN_EMAIL}`} className="text-[#7A2E2E] underline">{RETURN_EMAIL}</a>.
          If anything is unclear, reply to the email we sent you and we will gladly help.
        </p>
      </div>
    </Frame>
  );
};

export default SignDocument;
